import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import Skeleton from "@/components/shared/Skeleton";
import { Card } from "@/components/ui/Card";
import CopyId from "@/components/ui/CopyId";

import { TenantService } from "@/services/tenantService";
import useLiveResource from "@/hooks/useLiveResource";
import TenantInfoPanel from "./components/TenantInfoPanel";
import RolesPanel from "./components/RolesPanel";
import UsersPanel from "./components/UsersPanel";
import ModulesPanel from "./components/ModulesPanel";
import UsagePanel from "./components/UsagePanel";
import SectionHeader from "./components/SectionHeader";
import StatusBadge from "@/components/shared/StatusBadge";
import TabsBar from "./components/TabsBar";
import { ArrowLeft, Building2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import toast from "react-hot-toast";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

/* ====================== TABS CONFIG ====================== */
const TABS = [
  {
    id: "info",
    label: "Tenant Info",
    component: TenantInfoPanel,
  },
  {
    id: "rbac",
    label: "Roles & Permissions",
    component: RolesPanel,
  },
  {
    id: "users",
    label: "Users",
    component: UsersPanel,
  },
  {
    // What this tenant's plan includes, plus their guardian policy overrides.
    // Entitlement only — the fleet-wide kill switch lives on
    // /super-admin/platform-settings.
    id: "modules",
    label: "Modules",
    component: ModulesPanel,
  },
  {
    id: "usage",
    label: "Usage",
    component: UsagePanel,
  },
];

const TenantDetails = () => {
  const { tenantId } = useParams();
  const setHeader = useOutletContext();
  const navigate = useNavigate();

  // This page is opened from the tenant list, and also from the dashboard's
  // "recent tenants" card. `-1` returns to whichever it was. A direct hit on
  // the URL has no history entry to go back to, so it falls back to the list.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/super-admin/tenant");
  };

  const [tenantData, setTenantData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    if (!tenantId) return;
    fetchTenantDetails();
  }, [tenantId]);

  useEffect(() => {
    setHeader({
      title: isLoading ? "Tenant Details" : tenantData?.name || "Tenant Details",
      subTitle: tenantData?.slug || "",
    });

    return () => setHeader({ title: "", subTitle: "" });
  }, [tenantData, isLoading, setHeader]);

  // `silent` skips the loading skeleton — used for the background refetch a
  // live update triggers, so another SuperAdmin editing this tenant does not
  // flash the whole page back to its loading state.
  const fetchTenantDetails = async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const res = await TenantService.fetchTenantById(tenantId);

      const found = res.tenant;

      // Forwarded, not restated. The field-by-field copy that used to live
      // here had drifted from `GetTenantResponse` in both directions: it
      // dropped `enabledExtensionSites`, `sourcingUsesOwnApiKeys`, `admin`
      // and `totalUsers`, so the Extension Sites row read "None" for every
      // tenant however it was entitled, and it read `createdBy`, `roles` and
      // `users` off keys the response has never sent.
      //
      // Defaults stay, and only where a default means something: `dbMode`,
      // `provisioningStatus` and `dbInfo` are newer than some responses, and
      // `isDeleted` is coerced because the panel branches on it.
      setTenantData({
        ...found,
        isDeleted: !!found.isDeleted,
        deleteReason: found.deleteReason ?? null,
        dbMode: found.dbMode ?? "shared",
        provisioningStatus: found.provisioningStatus ?? "active",
        dbInfo: found.dbInfo ?? null,
      });
    } catch (err) {
      console.error("Failed to fetch tenant details:", err);
      toast.error(apiErrorMessageFormat(err, "Failed to fetch tenant details"));
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  // Another SuperAdmin editing or (de)activating this same tenant otherwise
  // only shows up here on the next manual reload.
  useLiveResource({
    keys: tenantId ? `tenant:${tenantId}` : [],
    enabled: !!tenantId,
    onChange: () => fetchTenantDetails({ silent: true }),
  });

  const activeTabConfig = TABS.find((t) => t.id === activeTab);
  const ActiveComponent = activeTabConfig?.component;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button
          onClick={handleBack}
          className="flex items-center gap-1.5 !px-3 !py-1.5 rounded-lg border border-border-primary bg-background-secondary text-[12px] text-text-primary hover:bg-background-tertiary"
        >
          <ArrowLeft size={14} />
          Back
        </Button>
      </div>

      <Card className="!p-0 overflow-hidden bg-background-secondary border-border-primary">
        <SectionHeader title="Tenant Details" right={isLoading ? <Skeleton className="h-6 w-20" /> : <StatusBadge isActive={tenantData?.isActive} />} />

        <div className="p-5 flex flex-col gap-5">
          {/* Top Summary */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-background-secondary border border-border-primary flex items-center justify-center text-text-secondary">
                <Building2 width={15} height={15} />
              </div>

              <div className="flex flex-col">
                <p className="text-[14px] font-semibold text-text-primary m-0 break-all max-w-[280px] lg:max-w-[600px]">
                  {isLoading ? <Skeleton className="h-5 w-40 bg-background-tertiary" /> : tenantData?.name}
                  <CopyId label="Tenant" value={tenantId} />
                </p>
                <p className="text-[12px] text-text-muted mt-1 m-0 flex items-center gap-2">
                  {isLoading ? (
                    <>
                      <Skeleton className="h-4 w-24 bg-background-tertiary rounded-md" />
                      <span>•</span>
                      <Skeleton className="h-4 w-20 bg-background-tertiary rounded-md" />
                    </>
                  ) : (
                    <>
                      <span>{tenantData?.slug}</span>
                      <span>•</span>
                      <span>{tenantData?.region}</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            <TabsBar activeTab={activeTab} onTabChange={setActiveTab} tabs={TABS} />
          </div>

          <div className="animate-in fade-in duration-300">{ActiveComponent && <ActiveComponent tenantData={tenantData} tenantId={tenantId} />}</div>
        </div>
      </Card>
    </div>
  );
};

export default TenantDetails;
